// Landing.jsx — Page d'accueil AssoPerf (redesign éditorial premium)
// Direction artistique : "Swiss & High-Contrast" + "Editorial Elegance"
// Inspirations : Linear / Vercel / Le Monde / Service-Public.fr
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowRight, Check, Sparkles, Zap, Globe, Smartphone, CreditCard, BookOpen,
  Users, MessageCircle, Calendar, Bell, ShieldCheck, ChevronDown, ExternalLink, X, Play,
} from 'lucide-react';

import { Logo } from '@/components/Logo';
import { useAuth } from '@/lib/auth';
import { roleHome } from '@/lib/roleHome';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Démos prospects (REF Figeac exclu : vraie asso cliente)
const DEMO_ASSOS = [
  {
    slug: 'club-sport-demo',
    nom: 'Club Sport Demo',
    secteur: 'Sport associatif',
    accroche: 'Un club sportif vivant et bienveillant',
    description: 'Adhérents, groupes, appels, événements & billetterie. Découvrez chaque rôle du club.',
    couleur: '#10B981',
    icon: '⚽',
    roles: [
      { key: 'sport_president', label: 'Président', icon: '👑' },
      { key: 'sport_bureau', label: 'Bureau', icon: '📋' },
      { key: 'sport_educateur', label: 'Éducateur', icon: '🏃' },
      { key: 'sport_joueur', label: 'Joueur', icon: '⚽' },
      { key: 'sport_parent', label: 'Parent', icon: '👪' },
    ],
  },
  {
    slug: 'reseau-pro-demo',
    nom: 'Réseau Pro Lyon',
    secteur: 'Réseau d\'affaires',
    accroche: 'Le club business des décideurs',
    description: 'Annuaire pro, Speed Business intelligent, networking, événements adhérents-only.',
    couleur: '#0047FF',
    icon: '💼',
    roles: [
      { key: 'pro_president', label: 'Président', icon: '👑' },
      { key: 'pro_bureau', label: 'Bureau', icon: '📋' },
      { key: 'pro_membre', label: 'Membre', icon: '💼' },
    ],
  },
  {
    slug: 'culture-demo',
    nom: 'Les Amis de la Culture',
    secteur: 'Association culturelle',
    accroche: 'Cultivons ensemble nos passions',
    description: 'Gazette, sponsors, billetterie spectacle, gestion bénévoles & adhésions famille.',
    couleur: '#9333EA',
    icon: '🎭',
    roles: [
      { key: 'culture_president', label: 'Président', icon: '👑' },
      { key: 'culture_bureau', label: 'Bureau', icon: '📋' },
      { key: 'culture_benevole', label: 'Bénévole', icon: '🙌' },
      { key: 'culture_adherent', label: 'Adhérent', icon: '🎟️' },
    ],
  },
];

export default function Landing() {
  const [showInscrire, setShowInscrire] = useState(false);
  const [showDemos, setShowDemos] = useState(false);
  const [billing, setBilling] = useState('monthly'); // monthly | annual
  const [demoLoadingKey, setDemoLoadingKey] = useState(null);
  const navigate = useNavigate();
  const { demoLogin } = useAuth();

  const handleDemoAccess = async (key) => {
    if (demoLoadingKey) return;
    setDemoLoadingKey(key);
    try {
      const u = await demoLogin(key);
      navigate(roleHome(u.role));
    } catch {
      toast.error("Accès démo indisponible pour le moment. Réessayez.");
      setDemoLoadingKey(null);
    }
  };

  // Scroll smooth
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => { document.documentElement.style.scrollBehavior = ''; };
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F9F8] text-[#0F172A] font-sans antialiased" data-testid="landing-page">

      {/* ─────────────── NAVBAR ─────────────── */}
      <Navbar onRegister={() => setShowInscrire(true)} onDemos={() => setShowDemos(true)} />

      {/* ─────────────── HERO ─────────────── */}
      <Hero onRegister={() => setShowInscrire(true)} onDemos={() => setShowDemos(true)} />

      {/* ─────────────── MODAL DÉMOS ─────────────── */}
      {showDemos && <DemoSelectorModal onClose={() => setShowDemos(false)} onDemoAccess={handleDemoAccess} demoLoadingKey={demoLoadingKey} />}

      {/* ─────────────── KILLER FEATURES (4 colonnes brèves) ─────────────── */}
      <KillerFeatures />

      {/* ─────────────── BENTO FEATURES ─────────────── */}
      <Features />

      {/* ─────────────── UNIVERS / TYPES D'ASSOS ─────────────── */}
      <Univers />

      {/* ─────────────── PRICING ─────────────── */}
      <Pricing billing={billing} setBilling={setBilling} onRegister={() => setShowInscrire(true)} />

      {/* ─────────────── TÉMOIGNAGES VIDE (premiers retours bientôt) ─────────────── */}
      <Testimonials onRegister={() => setShowInscrire(true)} />

      {/* ─────────────── FAQ ─────────────── */}
      <FAQ />

      {/* ─────────────── CTA FINAL ─────────────── */}
      <FinalCTA onRegister={() => setShowInscrire(true)} />

      {/* ─────────────── FOOTER ─────────────── */}
      <Footer />

      {/* MODAL INSCRIPTION */}
      {showInscrire && <InscriptionModal onClose={() => setShowInscrire(false)} onSuccess={(slug) => { setShowInscrire(false); navigate('/login'); }} />}
    </div>
  );
}

// ─────────────────────── NAVBAR ───────────────────────
function Navbar({ onRegister, onDemos }) {
  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#E2E8F0]" data-testid="landing-nav">
      <div className="max-w-6xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="landing-logo">
          <Logo size={30} className="transition-transform group-hover:scale-105" />
          <span className="font-display font-black text-[#0F172A] tracking-tight">AssoPerf</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm">
          <a href="#fonctionnalites" className="text-[#475569] hover:text-[#0F172A] transition-colors">Fonctionnalités</a>
          <button type="button" onClick={onDemos} className="text-[#475569] hover:text-[#0F172A] transition-colors" data-testid="landing-nav-demos">Démos en direct</button>
          <a href="#univers" className="text-[#475569] hover:text-[#0F172A] transition-colors">Types d&apos;associations</a>
          <a href="#tarifs" className="text-[#475569] hover:text-[#0F172A] transition-colors">Tarifs</a>
          <a href="#faq" className="text-[#475569] hover:text-[#0F172A] transition-colors">FAQ</a>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" className="hidden sm:inline-flex px-4 py-2 text-sm font-semibold text-[#475569] hover:text-[#0F172A] transition-colors" data-testid="landing-login-btn">
            Se connecter
          </Link>
          <button
            onClick={onRegister}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#0F172A] hover:bg-[#0047FF] text-white text-sm font-bold transition-all shadow-sm hover:shadow"
            data-testid="landing-register-btn"
          >
            Créer mon espace
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </header>
  );
}

// ─────────────────────── HERO ───────────────────────
function Hero({ onRegister, onDemos }) {
  return (
    <section className="relative pt-20 sm:pt-28 pb-20 sm:pb-32 overflow-hidden">
      {/* Décor subtil — points typographiques */}
      <div className="absolute inset-0 -z-10" aria-hidden>
        <div className="absolute top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#0047FF]/5 blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
        {/* Colonne texte (asymétrique gauche) */}
        <div className="lg:col-span-7 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0047FF]/10 text-[#0047FF] text-xs font-bold uppercase tracking-[0.18em] mb-8" data-testid="hero-badge">
            <Sparkles className="w-3 h-3" />
            Plateforme SaaS pour associations
          </div>
          <h1
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight text-[#0F172A] leading-[0.98] mb-7"
            data-testid="hero-title"
          >
            L'espace de gestion <span className="italic font-medium text-[#0047FF]">de votre association.</span>
          </h1>
          <p className="text-lg sm:text-xl text-[#475569] leading-relaxed mb-9 max-w-xl">
            AssoPerf est l'outil tout-en-un pour gérer une association <strong className="text-[#0F172A] font-semibold">déjà déclarée</strong> : web app installable sur iPhone & Android, site public, comptabilité, paiements Stripe, agents IA — à vos couleurs.
          </p>
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <button
              onClick={onRegister}
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#0047FF] hover:bg-[#0040E0] text-white font-bold text-base transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
              data-testid="hero-cta-primary"
            >
              Créer mon espace de gestion
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              type="button"
              onClick={onDemos}
              className="inline-flex items-center gap-1.5 px-5 py-3.5 rounded-xl border border-[#E2E8F0] bg-white hover:border-[#0F172A] text-[#0F172A] font-semibold text-base transition-colors"
              data-testid="hero-cta-demo"
            >
              Voir comment ça marche
              <Play className="w-4 h-4" />
            </button>
          </div>
          <Link
            to="/creer-mon-association"
            className="inline-flex items-center gap-1 text-sm text-[#475569] hover:text-[#0047FF] underline underline-offset-4 decoration-[#94A3B8]/50 hover:decoration-[#0047FF] transition-colors"
            data-testid="hero-link-creer-asso"
          >
            Pas encore d'association ? Guide pour la créer en 5 étapes
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {/* Colonne mockup — preview minimale de l'app */}
        <div className="lg:col-span-5">
          <HeroMockup />
        </div>
      </div>
    </section>
  );
}

function HeroMockup() {
  return (
    <div className="relative">
      <div className="relative bg-white rounded-3xl border border-[#E2E8F0] shadow-xl shadow-[#0F172A]/5 p-1 transition-transform hover:-translate-y-1">
        {/* Barre de fenêtre */}
        <div className="flex items-center gap-1.5 px-4 py-2.5">
          <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#28C940]" />
          <div className="ml-auto text-[10px] text-[#94A3B8] font-mono">votre-asso.fr</div>
        </div>
        <div className="bg-gradient-to-b from-[#F9F9F8] to-white rounded-2xl p-5">
          {/* Faux dashboard — sans chiffres bidons */}
          <div className="mb-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0047FF] text-white font-bold grid place-items-center font-display">A</div>
            <div>
              <div className="text-sm font-bold text-[#0F172A]">Votre association</div>
              <div className="text-xs text-[#94A3B8]">Espace de gestion</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {[
              { icon: Users, label: 'Membres' },
              { icon: Calendar, label: 'Agenda' },
              { icon: CreditCard, label: 'Paiements' },
              { icon: BookOpen, label: 'Compta' },
            ].map(({ icon: Ic, label }) => (
              <div key={label} className="bg-white border border-[#E2E8F0] rounded-xl p-3 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#0047FF]/10 text-[#0047FF] grid place-items-center">
                  <Ic className="w-3.5 h-3.5" />
                </div>
                <span className="text-xs font-semibold text-[#0F172A]">{label}</span>
              </div>
            ))}
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">Prochaine réunion</div>
              <div className="text-[10px] text-[#0047FF] font-bold">À VENIR</div>
            </div>
            <div className="text-sm font-bold text-[#0F172A] mb-0.5">Assemblée mensuelle</div>
            <div className="text-xs text-[#475569]">Jeudi · 19h15</div>
          </div>
        </div>
      </div>
      {/* Étiquette flottante */}
      <div className="absolute -bottom-3 -left-3 bg-white border border-[#E2E8F0] rounded-xl px-3 py-2 shadow-lg flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-[#059669] animate-pulse" />
        <span className="text-xs font-semibold text-[#0F172A]">Web app installable</span>
      </div>
    </div>
  );
}

// ─────────────────────── KILLER FEATURES ───────────────────────
function KillerFeatures() {
  const items = [
    { icon: ShieldCheck, label: 'White-label', desc: '100% à vos couleurs' },
    { icon: Smartphone, label: 'PWA installable', desc: 'iPhone & Android' },
    { icon: CreditCard, label: 'Stripe natif', desc: 'Encaissements directs' },
    { icon: Sparkles, label: '4 agents IA', desc: 'Claude Sonnet 4.6' },
  ];
  return (
    <section className="border-y border-[#E2E8F0] bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
        {items.map(({ icon: Ic, label, desc }) => (
          <div key={label} className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#0047FF]/8 text-[#0047FF] grid place-items-center shrink-0">
              <Ic className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm font-bold text-[#0F172A]">{label}</div>
              <div className="text-xs text-[#475569] mt-0.5">{desc}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────── FEATURES BENTO ───────────────────────
function Features() {
  return (
    <section id="fonctionnalites" className="py-24 sm:py-32">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <SectionHeading
          label="Fonctionnalités"
          title={<>Tout ce dont votre <span className="italic font-medium text-[#0047FF]">association a besoin.</span></>}
          subtitle="Conçu pour les présidents, les trésoriers et les membres — chaque rôle a ses outils, sans complexité."
        />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mt-16">
          {/* Carte large */}
          <FeatureCard
            className="md:col-span-7"
            icon={Smartphone}
            tag="Web app installable"
            title="Une app à votre nom, dans la poche de vos membres."
            desc="Vos adhérents installent votre app en 1 clic sur iPhone ou Android (PWA). Messages, agenda, billets, carte de membre — tout à portée. Vos couleurs, votre logo, votre identité."
          />
          <FeatureCard
            className="md:col-span-5"
            icon={CreditCard}
            tag="Stripe Connect"
            title="Encaissements sur votre propre IBAN."
            desc="Chaque association connecte son propre compte Stripe. Adhésions, billetterie, paiements en 3× — directement sur votre compte. Aucun intermédiaire."
          />
          <FeatureCard
            className="md:col-span-5"
            icon={Sparkles}
            tag="Agents IA · Claude"
            title="4 assistants IA pour le bureau."
            desc="Relances cotisations rédigées, résumés de gazette, suggestions de sujets de réunion. Tous validés avant envoi."
            highlight
          />
          <FeatureCard
            className="md:col-span-7"
            icon={BookOpen}
            tag="Comptabilité"
            title="Compta simple, rapprochement bancaire automatique."
            desc="Import CSV de votre banque (CA, BNP, BP, La Banque Postale, SG), rapprochement automatique, justificatifs, export Excel pour votre expert-comptable. Le trésorier vous remerciera."
          />
          <FeatureCard
            className="md:col-span-6"
            icon={Globe}
            tag="Site public"
            title="Un site web à votre domaine."
            desc="Page publique avec votre charte, agenda, formulaire d'inscription, vitrine des membres entreprises. Référencé sur Google."
          />
          <FeatureCard
            className="md:col-span-6"
            icon={Users}
            tag="Multi-rôles"
            title="Président · Trésorier · Secrétaire · Membre."
            desc="Chacun voit ce qui le concerne. Le bureau garde le contrôle, les membres vivent la vie de l'association."
          />
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ icon: Ic, tag, title, desc, className = '', highlight = false }) {
  return (
    <div className={`group relative bg-white border ${highlight ? 'border-[#0047FF]/30' : 'border-[#E2E8F0]'} rounded-3xl p-7 lg:p-9 transition-all hover:shadow-lg hover:-translate-y-1 ${className}`}>
      <div className="flex items-center gap-3 mb-5">
        <div className={`w-10 h-10 rounded-xl ${highlight ? 'bg-[#0047FF] text-white' : 'bg-[#0047FF]/10 text-[#0047FF]'} grid place-items-center`}>
          <Ic className="w-5 h-5" />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0047FF]">{tag}</span>
      </div>
      <h3 className="font-display text-xl sm:text-2xl font-bold text-[#0F172A] mb-2.5 leading-tight">{title}</h3>
      <p className="text-sm text-[#475569] leading-relaxed">{desc}</p>
    </div>
  );
}

// ─────────────────────── UNIVERS ───────────────────────
function Univers() {
  const items = [
    { e: '🏛️', t: 'Réseaux pro & business clubs', d: 'BNI, Rotary, clubs d\'entrepreneurs.' },
    { e: '⚽', t: 'Clubs sportifs', d: 'Licences FFR, FFF, billetterie matchs.' },
    { e: '🎭', t: 'Culture & arts', d: 'Théâtre, musique, danse, festivals.' },
    { e: '🏥', t: 'Caritatif & humanitaire', d: 'Dons, bénévoles, événements solidaires.' },
  ];
  return (
    <section id="univers" className="py-24 sm:py-32 bg-white border-y border-[#E2E8F0]">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <SectionHeading
          label="Pour qui ?"
          title={<>Conçu pour <span className="italic font-medium text-[#0047FF]">toutes les associations.</span></>}
          subtitle="De 10 à 1000 membres. Sport, culture, caritatif, réseau professionnel. AssoPerf s'adapte à votre identité."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-14">
          {items.map((it) => (
            <div key={it.e} className="group bg-white border border-[#E2E8F0] rounded-2xl p-6 transition-all hover:border-[#0047FF]/40 hover:-translate-y-1" data-testid={`univers-${it.e}`}>
              <div className="text-3xl mb-4">{it.e}</div>
              <h3 className="font-display text-base font-bold text-[#0F172A] mb-1.5 leading-tight">{it.t}</h3>
              <p className="text-xs text-[#475569] leading-relaxed">{it.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────── PRICING ───────────────────────
function Pricing({ billing, setBilling, onRegister }) {
  const monthly = { starter: 20, pro: 45, network: 90 };
  const annual = { starter: 16, pro: 36, network: 72 };
  const prices = billing === 'annual' ? annual : monthly;
  return (
    <section id="tarifs" className="py-24 sm:py-32">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <SectionHeading
          label="Tarifs"
          title={<>Sans engagement, <span className="italic font-medium text-[#0047FF]">14 jours gratuits.</span></>}
          subtitle="Choisissez selon la taille de votre asso. Changez de plan à tout moment."
        />
        <div className="flex items-center justify-center mt-8 mb-12">
          <div className="inline-flex bg-[#E2E8F0]/60 rounded-full p-1">
            <button
              onClick={() => setBilling('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${billing === 'monthly' ? 'bg-white text-[#0F172A] shadow' : 'text-[#475569]'}`}
            >Mensuel</button>
            <button
              onClick={() => setBilling('annual')}
              className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${billing === 'annual' ? 'bg-white text-[#0F172A] shadow' : 'text-[#475569]'}`}
            >Annuel <span className="ml-1 text-[10px] text-[#059669]">-20%</span></button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <PricingCard
            name="Starter"
            price={prices.starter}
            tag="100 membres max"
            featured={false}
            feats={['Web app installable', 'Site public personnalisé', 'Paiements Stripe', 'Messagerie · Documents', 'Support email']}
            disabled={['Agents IA', 'Comptabilité avancée', 'Annuaire entreprises']}
            ctaLabel="Démarrer"
            onClick={onRegister}
            testid="plan-starter"
          />
          <PricingCard
            name="Pro"
            price={prices.pro}
            tag="500 membres max · populaire"
            featured
            feats={['Tout du Starter', 'Comptabilité + import bancaire', 'Carte membre NFC', 'Annuaire entreprises', 'Support prioritaire']}
            disabled={['Multi-événements simultanés']}
            ctaLabel="Choisir Pro"
            onClick={onRegister}
            testid="plan-pro"
          />
          <PricingCard
            name="Network"
            price={prices.network}
            tag="Réseaux pro · illimité"
            featured={false}
            feats={['Tout du Pro', '4 Agents IA (Claude)', 'Messagerie + DM + channels', 'Gazette + Agenda Pro', 'Multi-événements illimités']}
            disabled={[]}
            ctaLabel="Choisir Network"
            onClick={onRegister}
            testid="plan-network"
          />
        </div>
        <div className="text-center mt-8 text-xs text-[#94A3B8]">
          Tous les plans incluent 14 jours d'essai gratuits · Aucune carte requise · Annulable en 1 clic
        </div>
      </div>
    </section>
  );
}

function PricingCard({ name, price, tag, featured, feats, disabled = [], ctaLabel, onClick, testid }) {
  return (
    <div
      className={`relative bg-white border ${featured ? 'border-[#0047FF] shadow-xl shadow-[#0047FF]/10 scale-[1.02]' : 'border-[#E2E8F0]'} rounded-3xl p-8 transition-all hover:-translate-y-1`}
      data-testid={testid}
    >
      {featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#0047FF] text-white text-[10px] font-black uppercase tracking-[0.18em]">
          Recommandé
        </div>
      )}
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0047FF] mb-2">{tag}</div>
      <h3 className="font-display text-2xl font-bold text-[#0F172A] mb-4">{name}</h3>
      <div className="mb-6">
        <span className="font-display text-5xl font-black text-[#0F172A] tracking-tight">{price}</span>
        <span className="text-base text-[#475569] ml-1">€ HT / mois</span>
      </div>
      <ul className="space-y-2.5 mb-7">
        {feats.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-[#0F172A]">
            <Check className="w-4 h-4 text-[#059669] mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
        {disabled.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-[#94A3B8] line-through">
            <Check className="w-4 h-4 mt-0.5 shrink-0 opacity-40" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={onClick}
        className={`w-full py-3 rounded-xl font-bold text-sm transition-all ${featured ? 'bg-[#0047FF] hover:bg-[#0040E0] text-white shadow-md' : 'bg-[#0F172A] hover:bg-[#0047FF] text-white'}`}
        data-testid={`plan-cta-${name.toLowerCase()}`}
      >
        {ctaLabel} →
      </button>
    </div>
  );
}

// ─────────────────────── TÉMOIGNAGES (vide) ───────────────────────
function Testimonials({ onRegister }) {
  return (
    <section className="py-24 sm:py-32 bg-white border-y border-[#E2E8F0]">
      <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0047FF] mb-4">Et bientôt — vos témoignages</div>
        <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-[#0F172A] leading-tight mb-5">
          Premiers retours arrivent <span className="italic font-medium text-[#0047FF]">bientôt.</span>
        </h2>
        <p className="text-base sm:text-lg text-[#475569] leading-relaxed mb-9">
          Nous accompagnons nos premières associations dans leur mise en route. Vous serez parmi les pionniers ? Votre témoignage trouvera sa place ici très vite.
        </p>
        <button
          onClick={onRegister}
          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#0F172A] hover:bg-[#0047FF] text-white font-bold transition-all shadow-md"
          data-testid="testimonials-cta"
        >
          Devenir pionnier
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}

// ─────────────────────── FAQ ───────────────────────
function FAQ() {
  const items = [
    { q: 'Mes membres verront-ils "AssoPerf" quelque part ?', a: 'Jamais. Vos membres voient uniquement votre nom, votre logo, vos couleurs. Le site public est à votre domaine. L\'app affiche votre identité. AssoPerf est invisible — comme les coulisses d\'un théâtre.' },
    { q: 'Faut-il créer mon association sur AssoPerf ?', a: 'Non. AssoPerf est un outil de gestion pour les associations déjà déclarées en préfecture (loi 1901). Si vous n\'avez pas encore d\'asso, nous vous guidons gratuitement vers les services officiels (Service-Public.fr, JOAFE).' },
    { q: 'Comment fonctionnent les paiements Stripe ?', a: 'Chaque association connecte son propre compte Stripe (gratuit à créer). Les adhésions et la billetterie sont encaissées directement sur l\'IBAN de l\'asso — AssoPerf ne reçoit jamais l\'argent.' },
    { q: 'Et la sécurité des données ?', a: 'Hébergement européen (RGPD), chiffrement TLS, sauvegardes quotidiennes. Les clés API (Brevo, Stripe) sont stockées chiffrées. Aucune donnée n\'est jamais vendue.' },
    { q: 'Puis-je annuler à tout moment ?', a: 'Oui, en 1 clic depuis votre espace. Vous gardez l\'accès jusqu\'à la fin de votre période payée. Aucune question, aucun engagement.' },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-24 sm:py-32">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <SectionHeading
          label="FAQ"
          title={<>Questions <span className="italic font-medium text-[#0047FF]">fréquentes.</span></>}
        />
        <div className="mt-12 divide-y divide-[#E2E8F0] border-y border-[#E2E8F0]">
          {items.map((it, i) => (
            <div key={i} data-testid={`faq-item-${i}`}>
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 py-6 text-left"
              >
                <span className="font-display text-lg font-bold text-[#0F172A]">{it.q}</span>
                <ChevronDown className={`w-5 h-5 text-[#0047FF] shrink-0 transition-transform ${open === i ? 'rotate-180' : ''}`} />
              </button>
              {open === i && (
                <p className="text-base text-[#475569] leading-relaxed pb-6 pr-8">{it.a}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────── FINAL CTA ───────────────────────
function FinalCTA({ onRegister }) {
  return (
    <section className="py-24 sm:py-32">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="relative bg-[#0A192F] rounded-3xl overflow-hidden p-10 sm:p-16 text-center text-white">
          <div className="absolute inset-0 -z-0 opacity-30" style={{ background: 'radial-gradient(circle at 30% 30%, rgba(0,71,255,0.4), transparent 50%)' }} />
          <div className="relative z-10">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4DFFD2] mb-4">Prêt(e) à démarrer ?</div>
            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-tight mb-5 max-w-2xl mx-auto">
              Donnez à votre association les outils <span className="italic font-medium text-[#4DFFD2]">qu'elle mérite.</span>
            </h2>
            <p className="text-base sm:text-lg text-white/70 max-w-xl mx-auto mb-9 leading-relaxed">
              14 jours gratuits, aucune carte requise, configuration en 5 minutes.
            </p>
            <button
              onClick={onRegister}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-white hover:bg-[#0047FF] hover:text-white text-[#0F172A] font-bold transition-all shadow-xl hover:-translate-y-0.5"
              data-testid="final-cta-primary"
            >
              Créer mon espace de gestion gratuitement
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────── FOOTER ───────────────────────
function Footer() {
  return (
    <footer className="bg-[#0A192F] text-white/80 pt-16 pb-8">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-10">
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <Logo variant="white" size={30} />
              <span className="font-display font-black text-white">AssoPerf</span>
            </div>
            <p className="text-sm text-white/60 max-w-sm leading-relaxed">
              La plateforme SaaS qui donne à chaque association les outils des grandes organisations. Web app installable, site web, comptabilité, IA.
            </p>
          </div>
          <div>
            <h4 className="text-[10px] font-bold uppercase tracking-[0.18em] text-white mb-4">Produit</h4>
            <ul className="space-y-2 text-sm">
              <li><a href="#fonctionnalites" className="hover:text-[#4DFFD2]">Fonctionnalités</a></li>
              <li><a href="#tarifs" className="hover:text-[#4DFFD2]">Tarifs</a></li>
              <li><a href="#faq" className="hover:text-[#4DFFD2]">FAQ</a></li>
              <li><Link to="/creer-mon-association" className="hover:text-[#4DFFD2]">Créer son asso</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-[10px] font-bold uppercase tracking-[0.18em] text-white mb-4">Légal</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/mentions-legales" className="hover:text-[#4DFFD2]">Mentions légales</Link></li>
              <li><Link to="/cgu" className="hover:text-[#4DFFD2]">CGU</Link></li>
              <li><Link to="/confidentialite" className="hover:text-[#4DFFD2]">Confidentialité</Link></li>
              <li><a href="mailto:support@assoperf.fr" className="hover:text-[#4DFFD2]">Support</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 text-xs text-white/50">
          <div>
            © {new Date().getFullYear()} AssoPerf · Édité par <strong className="text-white/70">Osez Studio</strong> · SAS Market & Développement · 46100 Figeac
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#4DFFD2] animate-pulse" />Tous systèmes opérationnels</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─────────────────────── SECTION HEADING ───────────────────────
function SectionHeading({ label, title, subtitle }) {
  return (
    <div className="max-w-2xl">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0047FF] mb-4">{label}</div>
      <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0F172A] leading-[1.05] mb-5">{title}</h2>
      {subtitle && <p className="text-base sm:text-lg text-[#475569] leading-relaxed">{subtitle}</p>}
    </div>
  );
}

// ─────────────────────── MODAL INSCRIPTION ───────────────────────
function InscriptionModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({ asso_nom: '', prenom: '', nom: '', email: '', mot_de_passe: '', type: 'reseau', plan: 'starter', accept_cgu: false });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accept_cgu) return toast.error('Veuillez accepter les CGU');
    if (form.mot_de_passe.length < 8) return toast.error('Mot de passe : 8 caractères minimum');
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/register`, form);
      toast.success('Compte créé ! Connectez-vous.');
      onSuccess(form.asso_nom.toLowerCase().replace(/\s+/g, '-'));
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur lors de l\'inscription');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-md grid place-items-start justify-center p-4 sm:p-6 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl p-7 sm:p-9 max-w-md w-full my-6" data-testid="modal-inscrire">
        <div className="text-center mb-7">
          <Logo size={44} className="mx-auto mb-4" />
          <h2 className="font-display text-2xl font-bold text-[#0F172A] mb-1.5">Créer mon espace de gestion</h2>
          <p className="text-sm text-[#475569]">Pour les associations <strong className="text-[#0F172A]">déjà déclarées en préfecture</strong>. Prêt en 2 minutes.</p>
        </div>
        <div className="space-y-3.5">
          <Field label="Nom de l'association (telle que déclarée)">
            <input required type="text" value={form.asso_nom} onChange={(e) => setForm({ ...form, asso_nom: e.target.value })} className="ap-input" placeholder="Ex: Club Rugby Figeac, REF Lyon..." data-testid="modal-asso-nom" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Prénom">
              <input required type="text" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} className="ap-input" data-testid="modal-prenom" />
            </Field>
            <Field label="Nom">
              <input required type="text" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="ap-input" data-testid="modal-nom-admin" />
            </Field>
          </div>
          <Field label="Email">
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="ap-input" data-testid="modal-email" />
          </Field>
          <Field label="Mot de passe (8+ caractères)">
            <input required type="password" value={form.mot_de_passe} onChange={(e) => setForm({ ...form, mot_de_passe: e.target.value })} className="ap-input" minLength={8} />
          </Field>
          <Field label="Type d'association">
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="ap-input" data-testid="modal-type">
              <option value="reseau">Réseau professionnel</option>
              <option value="sportif">Sportif</option>
              <option value="culture">Culture & arts</option>
              <option value="caritatif">Caritatif</option>
              <option value="autre">Autre</option>
            </select>
          </Field>
          <Field label="Plan">
            <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="ap-input" data-testid="modal-plan">
              <option value="starter">Starter (20€/mois)</option>
              <option value="pro">Pro (45€/mois)</option>
              <option value="network">Network (90€/mois)</option>
            </select>
          </Field>
          <label className="flex items-start gap-2 text-xs text-[#475569] cursor-pointer">
            <input type="checkbox" checked={form.accept_cgu} onChange={(e) => setForm({ ...form, accept_cgu: e.target.checked })} className="mt-0.5" required />
            <span>J'accepte les <Link to="/cgu" target="_blank" className="text-[#0047FF] underline">CGU</Link> et la <Link to="/confidentialite" target="_blank" className="text-[#0047FF] underline">politique de confidentialité</Link>.</span>
          </label>
        </div>
        <div className="flex gap-2 justify-end mt-7">
          <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-semibold text-[#475569] hover:bg-[#E2E8F0]/50">Annuler</button>
          <button type="submit" disabled={submitting} className="px-6 py-2.5 rounded-xl bg-[#0047FF] hover:bg-[#0040E0] text-white text-sm font-bold disabled:opacity-50 shadow-md" data-testid="modal-submit">
            {submitting ? 'Création…' : 'Créer mon espace →'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

// ─────────────────────── DEMO SELECTOR MODAL ───────────────────────
function DemoSelectorModal({ onClose, onDemoAccess, demoLoadingKey }) {
  return (
    <div className="fixed inset-0 z-[80] bg-[#0F172A]/80 backdrop-blur-md grid place-items-center p-3 sm:p-6 fade-in overflow-y-auto" onClick={onClose} data-testid="demo-selector-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full my-6 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 sm:px-8 py-6 border-b border-[#F1F5F9] flex items-start justify-between gap-3">
          <div>
            <div className="inline-block text-[10px] font-bold uppercase tracking-[0.2em] text-[#0047FF] mb-2">Démos en direct</div>
            <h2 className="font-display text-2xl sm:text-3xl font-black text-[#0F172A]">Testez chaque interface en 1 clic</h2>
            <p className="text-sm text-[#475569] mt-1">Trois associations fictives, prêtes à explorer. Choisissez un rôle : vous êtes connecté instantanément, sans mot de passe.</p>
          </div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A] shrink-0" aria-label="Fermer" data-testid="demo-modal-close"><X className="w-5 h-5" /></button>
        </div>

        {/* 3 démos cards */}
        <div className="p-6 sm:p-8 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="demo-cards-grid">
          {DEMO_ASSOS.map((d) => (
            <div
              key={d.slug}
              className="border-2 border-[#F1F5F9] rounded-xl overflow-hidden bg-white flex flex-col"
              data-testid={`demo-card-${d.slug}`}
            >
              {/* Header gradient */}
              <div className="h-24 grid place-items-center text-5xl relative" style={{ background: `linear-gradient(135deg, ${d.couleur} 0%, ${d.couleur}cc 100%)` }}>
                <span>{d.icon}</span>
                <span className="absolute top-2 right-2 text-[9px] uppercase tracking-wider font-bold text-white/80 bg-black/20 px-2 py-0.5 rounded">{d.secteur}</span>
              </div>
              {/* Body */}
              <div className="p-4 flex flex-col flex-1">
                <h3 className="font-display font-black text-[#0F172A] text-base mb-1">{d.nom}</h3>
                <p className="text-xs font-semibold mb-2" style={{ color: d.couleur }}>« {d.accroche} »</p>
                <p className="text-xs text-[#475569] leading-relaxed mb-3">{d.description}</p>

                {/* Role buttons */}
                <div className="mt-auto space-y-1.5">
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#94A3B8] mb-1">Entrer en tant que</div>
                  {d.roles.map((r) => {
                    const loading = demoLoadingKey === r.key;
                    const disabled = !!demoLoadingKey;
                    return (
                      <button
                        key={r.key}
                        onClick={() => onDemoAccess(r.key)}
                        disabled={disabled}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-bold text-[#0F172A] transition-all hover:text-white disabled:opacity-60"
                        style={{ borderColor: `${d.couleur}55` }}
                        onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.background = d.couleur; e.currentTarget.style.borderColor = d.couleur; } }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = ''; e.currentTarget.style.borderColor = `${d.couleur}55`; }}
                        data-testid={`demo-role-${r.key}`}
                      >
                        <span className="text-base leading-none">{r.icon}</span>
                        <span className="flex-1 text-left">{r.label}</span>
                        {loading ? <span className="text-xs">…</span> : <ArrowRight className="w-3.5 h-3.5 opacity-60" />}
                      </button>
                    );
                  })}
                </div>

                <a
                  href={`/asso/${d.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-1 mt-3 pt-3 border-t border-[#F1F5F9] text-xs font-bold hover:underline"
                  style={{ color: d.couleur }}
                  data-testid={`demo-vitrine-${d.slug}`}
                >
                  Voir la vitrine publique <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Footer note */}
        <div className="px-6 sm:px-8 py-4 bg-[#F9F9F8] border-t border-[#F1F5F9] flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-[#475569]">
            <strong>Comptes de démonstration</strong> — les données sont fictives et partagées : n&apos;y saisissez pas d&apos;informations réelles.
          </p>
          <button onClick={onClose} className="text-xs font-semibold text-[#0F172A] hover:text-[#0047FF] transition-colors" data-testid="demo-modal-skip">Retour à l&apos;accueil →</button>
        </div>
      </div>
    </div>
  );
}
